import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Globe, Copy, Eye, EyeOff, RefreshCw, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ENDPOINT = `${BACKEND_URL}/api/public/lead-capture`;

const CodeBlock = ({ code, testId }) => (
  <div className="relative group">
    <pre className="bg-muted rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all" data-testid={testId}>{code}</pre>
    <Button size="sm" variant="ghost" className="absolute top-1.5 right-1.5 h-7 w-7 p-0 opacity-60 group-hover:opacity-100"
      onClick={() => { navigator.clipboard.writeText(code); toast.success('Copied'); }} data-testid={`${testId}-copy`}>
      <Copy className="h-3.5 w-3.5" />
    </Button>
  </div>
);

export const LeadCaptureSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/lead-capture/config`)
      .then(res => setCfg(res.data))
      .catch(e => setError(e.response?.status === 403 ? 'Admin access required' : 'Failed to load configuration'));
  }, []);

  if (error) return <Card><CardContent className="py-8 text-center text-muted-foreground" data-testid="lead-capture-error">{error}</CardContent></Card>;
  if (!cfg) return <Card><CardContent className="py-8 text-center text-muted-foreground">Loading…</CardContent></Card>;

  const toggleEnabled = async (v) => {
    try {
      const res = await axios.put(`${API}/lead-capture/config`, { enabled: v });
      setCfg({ ...cfg, ...res.data });
      toast.success(v ? 'Lead capture enabled' : 'Lead capture disabled');
    } catch { toast.error('Update failed'); }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate the API key? Your website will stop submitting leads until it uses the new key.')) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/lead-capture/regenerate-key`);
      setCfg({ ...cfg, ...res.data });
      toast.success('New API key generated');
    } catch { toast.error('Regeneration failed'); }
    finally { setRegenerating(false); }
  };

  const maskedKey = showKey ? cfg.api_key : `${cfg.api_key.slice(0, 8)}${'•'.repeat(20)}`;

  const curlExample = `curl -X POST "${ENDPOINT}" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{"name":"Jane Doe","email":"jane@example.com","phone":"+27821234567","company":"Acme Ltd","message":"Please contact me about your training programmes."}'`;

  const jsExample = `fetch("${ENDPOINT}", {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-API-Key": "YOUR_API_KEY" },
  body: JSON.stringify({
    name: form.name,        // required
    email: form.email,      // email or phone required
    phone: form.phone,
    company: form.company,
    message: form.message,
    website: ""             // honeypot — always leave empty
  })
}).then(r => r.json());`;

  return (
    <div className="space-y-4" data-testid="lead-capture-settings">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Globe className="h-4 w-4 text-primary" /> Website Lead Capture</CardTitle>
            <CardDescription>Route your website contact form submissions straight into the CRM as new leads (source: Website)</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="capture-enabled" className="text-sm">Enabled</Label>
            <Switch id="capture-enabled" checked={cfg.enabled} onCheckedChange={toggleEnabled} data-testid="lead-capture-enabled-toggle" />
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Total website leads</p>
              <p className="text-2xl font-bold" data-testid="lead-capture-total">{cfg.stats?.total_website_leads ?? 0}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Last 7 days</p>
              <p className="text-2xl font-bold" data-testid="lead-capture-last7">{cfg.stats?.last_7_days ?? 0}</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Endpoint URL</Label>
            <CodeBlock code={ENDPOINT} testId="lead-capture-endpoint" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1"><KeyRound className="h-3 w-3" /> API Key (send as X-API-Key header)</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted rounded-lg px-3 py-2 text-xs break-all" data-testid="lead-capture-api-key">{maskedKey}</code>
              <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setShowKey(!showKey)} data-testid="lead-capture-key-toggle">
                {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="outline" className="h-8 w-8 p-0"
                onClick={() => { navigator.clipboard.writeText(cfg.api_key); toast.success('API key copied'); }} data-testid="lead-capture-key-copy">
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={regenerate} disabled={regenerating} data-testid="lead-capture-regenerate-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? 'animate-spin' : ''}`} /> Regenerate
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">cURL example (for your developer)</Label>
            <CodeBlock code={curlExample} testId="lead-capture-curl-example" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">JavaScript (fetch) example</Label>
            <CodeBlock code={jsExample} testId="lead-capture-js-example" />
          </div>

          <p className="text-xs text-muted-foreground">
            Notes: <b>name</b> is required plus at least an email or phone. Spam protection is built in (honeypot field + max 10 submissions per hour per IP).
            Repeat enquiries from the same email within 24h are merged into the existing lead. Admins receive an email alert for every new website lead.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
